import {ChangeDetectionStrategy, Component, computed, input, output} from '@angular/core';
import {BzSelectOption} from '@bringoz/ui';
import * as _ from 'lodash';
import * as moment from 'moment-timezone';
import {injectLegacy} from '../../utils/inject-legacy.util';
import type {SplitService} from '../../../services/split-service.service';

interface HourMinute {
  hour: number;
  minute: number;
}

/**
 * Hour and minute picker for a timestamp, read and written in a timezone - `zoneId`, or the user's
 * selected one. The date part comes from `referenceDate`, so changing the time never moves the day.
 */
require('./bringoz-time-dropdown.scss');
@Component({
  standalone: false,
  selector: 'app-bringoz-time-dropdown',
  templateUrl: './bringoz-time-dropdown.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BringozTimeDropdownComponent {

  readonly timeStamp = input<number>();
  readonly referenceDate = input<number>();
  readonly zoneId = input<string>();
  readonly isDisabled = input<boolean>(false);

  readonly timeChange = output<number>();

  private readonly splitService = injectLegacy<SplitService>('splitService');

  readonly hourOptions: BzSelectOption<number>[];
  readonly minuteOptions: BzSelectOption<number>[] = _.times(60, (minute) => ({
    value: minute,
    label: _.padStart(_.toString(minute), 2, '0')
  }));

  readonly selected = computed<HourMinute>(() => {
    const timeStamp = this.timeStamp();
    if (!timeStamp) {
      return {hour: 0, minute: 0};
    }
    const time = moment.tz(timeStamp, this.timeZone());
    return {hour: time.hour(), minute: time.minute()};
  });

  constructor() {
    const is12Hour = this.splitService.getDateAndTimeConfig().time === 'hh:mm A';
    this.hourOptions = _.times(24, (hour) => ({value: hour, label: this.formatHourLabel(hour, is12Hour)}));
  }

  onHourChange(hour: unknown) {
    this.emitTime({hour: hour as number, minute: this.selected().minute});
  }

  onMinuteChange(minute: unknown) {
    this.emitTime({hour: this.selected().hour, minute: minute as number});
  }

  private emitTime({hour, minute}: HourMinute) {
    const baseDate = this.referenceDate() || this.timeStamp();
    if (!baseDate || _.isNil(hour) || _.isNil(minute)) {
      return;
    }
    this.timeChange.emit(moment.tz(baseDate, this.timeZone())
      .set({hour, minute, second: 0, millisecond: 0})
      .valueOf());
  }

  // Read live - the user may switch timezone while the screen is open
  private timeZone(): string {
    return this.zoneId() || this.splitService.getTimeZone();
  }

  /** "12 AM", "01 AM" … "11 PM" for a 12h site, "00" … "23" otherwise. */
  private formatHourLabel(hour: number, is12Hour: boolean): string {
    if (!is12Hour) {
      return _.padStart(_.toString(hour), 2, '0');
    }
    return `${_.padStart(_.toString(hour % 12 || 12), 2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
  }
}
